// Read-side hooks for the public blog (list + detail).

import { useInfiniteQuery, useQueries, useQuery } from "@tanstack/react-query";
import { getPublicBlogs, getPublicBlogPost, type BlogPost } from "@/lib/admin";
import { queryKeys } from "@/lib/query/query-keys";

export const BLOG_PAGE_SIZE = 12;

// Tag-filtered, "load more" paginated blog index. Each page requests the next
// `offset` derived from how many posts are already loaded.
export function useBlogPosts(tag: string) {
  return useInfiniteQuery({
    queryKey: queryKeys.blog.list(tag),
    queryFn: ({ pageParam }) =>
      getPublicBlogs({
        tag: tag === "All" ? undefined : tag,
        limit: BLOG_PAGE_SIZE,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, p) => n + p.posts.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
  });
}

// The latest N posts (e.g. the careers page teaser row).
export function useLatestBlogPosts(limit: number) {
  return useQuery({
    queryKey: queryKeys.blog.latest(limit),
    queryFn: () => getPublicBlogs({ limit }).then((res) => res.posts),
  });
}

// A single post by slug. Missing posts 404 - don't retry.
export function useBlogPost(slug: string) {
  return useQuery({
    queryKey: queryKeys.blog.detail(slug),
    queryFn: () => getPublicBlogPost(slug),
    retry: false,
  });
}

// A fixed set of posts pinned by slug, in the order given (the careers page
// row is curated, not "latest"). Slugs that fail to load are dropped.
export function usePinnedBlogPosts(slugs: readonly string[]) {
  return useQueries({
    queries: slugs.map((slug) => ({
      queryKey: queryKeys.blog.detail(slug),
      queryFn: () => getPublicBlogPost(slug),
      retry: false,
    })),
    combine: (results) => ({
      data: results.map((r) => r.data).filter((p): p is BlogPost => Boolean(p)),
    }),
  });
}
